import React, { useEffect, useRef, useState } from 'react';
import { Loader, Save, X } from '../../icons';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { LinkPreviewCard } from './LinkPreviewCard';
import {
  sanitizeUrlInput,
  detectPlatform,
  detectContentKind,
  isGoogleDocsUrl,
  ALLOWED_PLATFORMS_BY_MODE,
  type RequirePlatform,
} from '../../lib/linkPreview';

export type { RequirePlatform };

export type UrlInputModalProps = {
  open: boolean;
  /** Called when the user dismisses without saving (Esc, Cancel, backdrop). */
  onClose: () => void;
  /** Called with the sanitized URL string when the user clicks Save. */
  onSave: (url: string) => Promise<void> | void;
  /** Title shown in the modal header (e.g. "Add content URL"). */
  title: string;
  /** Optional one-line subtitle / description under the title. */
  description?: string;
  /** Initial value populated into the input — useful for re-submit flows. */
  initialUrl?: string;
  /** Placeholder shown in the input when empty. */
  placeholder?: string;
  /** Label on the primary action button. Defaults to "Save". */
  saveLabel?: string;
  /** When set, restricts the URL to the named platform's recognised domains
   *  (see ALLOWED_PLATFORMS_BY_MODE in lib/linkPreview for the actual host
   *  mapping — this is just the mode selector):
   *  'youtube'          → YouTube URL or Google Drive (content-review submission)
   *  'instagram'        → Google Drive only (content-review submission)
   *  'youtube-live'     → YouTube URL only, no Drive (live monitor URL)
   *  'instagram-live'   → instagram.com only, no Drive (live monitor URL)
   *  undefined          → any well-formed HTTPS URL
   */
  requirePlatform?: RequirePlatform;
  /** This row's own committed content kind (from
   *  RosterEntryLive.expected_content_kind / RosterEntryContent.expected_content_kind).
   *  When set to 'short' or 'reel', Save is blocked unless the pasted URL's
   *  own shape matches — mirrors the BE's validateContentKindURL exactly
   *  (asymmetric: a 'video'/'post' expectation does not itself constrain the
   *  URL shape, matching the BE). Applies in both live modes and
   *  content-review modes ('youtube' / 'instagram'); a Drive link is always
   *  exempt (detectContentKind returns null for it — Drive can't reveal a
   *  shape). Absent/undefined skips the check. */
  expectedContentKind?: string;
};

// Save-time error text per mode — kept separate from ALLOWED_PLATFORMS_BY_MODE
// so the allow-list can stay pure data.
const VALIDATION_ERROR_BY_MODE: Record<
  RequirePlatform,
  string
> = {
  youtube: 'Enter a YouTube video URL or Google Drive URL.',
  instagram: 'Enter a Google Drive URL for this Instagram campaign.',
  'youtube-live': 'Enter a YouTube video URL for this live post.',
  'instagram-live': 'Enter an Instagram post or Reel URL for this live post.',
};

// Red error border; the focus-visible variant out-specifies the Input's focus border.
const INVALID_INPUT_CLASS =
  'aria-invalid:border-destructive aria-invalid:focus-visible:border-destructive aria-invalid:focus-visible:ring-destructive/20';
const URL_ERROR_ID = 'url-input-error';

/** Links the input to its error message for screen readers, only when shown. */
function urlErrorId(error: string): string | undefined {
  if (!error) return undefined;
  return URL_ERROR_ID;
}

const GOOGLE_DOCS_ERROR = 'Enter a video URL or Google Drive URL. Google Docs links are not supported.';

// Content-kind mismatch text — mirrors the BE's validateContentKindURL
// wording exactly. Asymmetric on purpose (see expectedContentKind's doc
// comment): only 'short' and 'reel' expectations constrain the URL shape.
const CONTENT_KIND_ERROR: Record<'short' | 'reel', string> = {
  short: 'Enter a YouTube Shorts URL for this campaign.',
  reel: 'Enter an Instagram Reel URL for this campaign.',
};

/** Exported for unit tests — see UrlInputModal.test.ts. */
export function validateUrl(
  trimmed: string,
  requirePlatform: RequirePlatform | undefined,
  expectedContentKind?: string,
): string | null {
  try {
    new URL(trimmed);
  } catch {
    return 'Enter a valid URL, including https://.';
  }
  if (isGoogleDocsUrl(trimmed)) return GOOGLE_DOCS_ERROR;
  if (!requirePlatform) return null; // undefined mode — any well-formed HTTPS URL
  const allowed = ALLOWED_PLATFORMS_BY_MODE[requirePlatform];
  const platform = detectPlatform(trimmed);
  if (!platform || !allowed.has(platform)) {
    return VALIDATION_ERROR_BY_MODE[requirePlatform];
  }
  // Content-kind cross-check — live AND content-review modes. A confirmed
  // mismatch (never an undetectable shape, e.g. a Drive link — see
  // detectContentKind's doc comment) blocks Save, matching the BE's
  // validateContentKindURL enforcement exactly (AdminSetMonitorURL for live,
  // UploadContent for content-review).
  const YOUTUBE_MODES: RequirePlatform[] = ['youtube', 'youtube-live'];
  const INSTAGRAM_MODES: RequirePlatform[] = ['instagram', 'instagram-live'];
  if (
    (expectedContentKind === 'short' && YOUTUBE_MODES.includes(requirePlatform)) ||
    (expectedContentKind === 'reel' && INSTAGRAM_MODES.includes(requirePlatform))
  ) {
    const detected = detectContentKind(trimmed);
    if (detected && detected !== expectedContentKind) {
      return CONTENT_KIND_ERROR[expectedContentKind];
    }
  }
  return null;
}

// Lightweight URL paste modal — used by ContentTab's per-row "Add content URL"
// action and LiveContentTab's "Add live URL" action. Replaces window.prompt.
// Built on the shadcn Dialog so the background scroll-locks + inerts and focus
// is trapped while open.

export function UrlInputModal({
  open,
  onClose,
  onSave,
  title,
  description,
  initialUrl = '',
  placeholder = 'https://…',
  saveLabel = 'Save',
  requirePlatform,
  expectedContentKind,
}: UrlInputModalProps) {
  const [url, setUrl] = useState(initialUrl);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset local state every time the modal (re)opens — also seeds the
  // input with whatever initialUrl the caller passed for THIS open.
  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset is coupled with imperative rAF focus/select + cleanup, not a pure state sync
    setUrl(initialUrl);
    setError('');
    setBusy(false);
    // Auto-focus the input so the admin can paste immediately. Wrap
    // in rAF so the input is mounted before we focus.
    const id = requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      // Only select the pre-filled value — if the value has already
      // changed by the time this fires (fast typing/paste racing the rAF),
      // selecting now would highlight what the admin just typed and the
      // very next keystroke would overwrite it.
      if (el.value === initialUrl) el.select();
    });
    return () => cancelAnimationFrame(id);
  }, [open, initialUrl]);

  const handleSave = async () => {
    const trimmed = sanitizeUrlInput(url);
    if (!trimmed) {
      setError('Enter a URL to continue.');
      return;
    }
    const validationError = validateUrl(trimmed, requirePlatform, expectedContentKind);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setBusy(true);
    try {
      await onSave(trimmed);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'We could not save this URL. Check the link and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onClose(); }}>
      <DialogContent showCloseButton={false} className="max-w-md gap-0 overflow-hidden rounded-2xl p-0">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <DialogTitle className="text-body-md-semibold text-foreground">{title}</DialogTitle>
            {description ? (
              <DialogDescription className="mt-0.5 text-caption-regular text-muted-foreground">
                {description}
              </DialogDescription>
            ) : null}
          </div>
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="size-9 shrink-0 rounded-lg border border-border text-muted-foreground hover:bg-accent"
          >
            <X size={18} />
          </Button>
        </div>

        {/* Body */}
        <div className="p-5">
          <label htmlFor="url-input-field" className="block text-caption-medium uppercase tracking-wider text-muted-foreground">
            URL
          </label>
          <Input
            id="url-input-field"
            ref={inputRef}
            type="url"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              if (error) setError('');
            }}
            disabled={busy}
            placeholder={placeholder}
            // Enter saves (single-input form, no accidental-submit risk).
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !busy) {
                e.preventDefault();
                void handleSave();
              }
            }}
            aria-invalid={Boolean(error)}
            aria-describedby={urlErrorId(error)}
            className={cn('mt-2', INVALID_INPUT_CLASS)}
          />
          {error ? (
            <p id={URL_ERROR_ID} className="mt-2 text-caption-medium text-destructive">
              {error}
            </p>
          ) : null}
          {/* Preview the SANITIZED value against the same platform gate Save
              enforces, so we never render a working embed for a URL Save will
              reject (e.g. an IG post in Drive-only 'instagram' review mode). */}
          <LinkPreviewCard url={sanitizeUrlInput(url)} requirePlatform={requirePlatform} />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border bg-muted px-5 py-3">
          <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="default"
            type="button"
            onClick={() => void handleSave()}
            disabled={busy || !url.trim()}
            className="gap-1.5"
          >
            {busy ? (
              <>
                <Loader size={12} className="animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save size={12} />
                {saveLabel}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
